import * as C from './constants'
import type { Entity, GameState } from './types'

/**
 * Canvas 2D renderer with a pinhole-camera projection: the road is drawn as
 * screen-space quads whose corners are projected from world metres, which
 * looks three-dimensional while staying trivially cheap on a phone GPU.
 *
 *   scale = FOCAL * height / (z + CAM_Z)      [px per metre at distance z]
 *   sx    = centreX + x * scale
 *   sy    = horizonY + CAM_HEIGHT * scale
 */

/** Camera sits behind the truck's rear axle. */
const CAM_Z = C.TRUCK_DEPTH + C.CAM_BACK
/** Nothing closer than this is projected — it would be behind the lens. */
const NEAR_Z = -CAM_Z + 8
/** Half width of the tarmac including shoulders, metres. */
const ROAD_HALF = 1.5 * C.LANE_WIDTH + 1.15
/** Metres between the scrolling lane dashes. */
const DASH_PERIOD = 14
const DASH_LENGTH = 6

export interface Renderer {
  resize(): void
  draw(state: GameState, elapsed: number): void
}

interface Star {
  x: number
  y: number
  r: number
  a: number
}

interface Building {
  x: number
  w: number
  h: number
}

export function createRenderer(canvas: HTMLCanvasElement): Renderer {
  // Bound to a non-nullable const: the hoisted draw helpers below capture it,
  // and TypeScript does not carry narrowing into hoisted function bodies.
  const maybeCtx = canvas.getContext('2d', { alpha: false })
  if (!maybeCtx) throw new Error('2D canvas context unavailable')
  const ctx: CanvasRenderingContext2D = maybeCtx

  let w = 0
  let h = 0
  let horizonY = 0
  let centreX = 0
  let focal = 0
  let sky: CanvasGradient | null = null
  let ground: CanvasGradient | null = null
  let tarmac: CanvasGradient | null = null
  let headlights: CanvasGradient | null = null
  let vignette: CanvasGradient | null = null
  /** Horizon bloom, rebuilt only when the speed bucket changes. */
  let bloom: CanvasGradient | null = null
  let bloomBucket = -1
  let stars: Star[] = []
  let skyline: Building[] = []
  /** Advances with distance so decorations scroll without per-frame randomness. */
  let shimmer = 0

  const rng = (seed: number) => {
    let a = seed >>> 0
    return () => {
      a = (a + 0x9e3779b9) >>> 0
      let t = Math.imul(a ^ (a >>> 16), 2246822507)
      t ^= Math.imul(t ^ (t >>> 13), 3266489909)
      return ((t ^ (t >>> 16)) >>> 0) / 4294967296
    }
  }

  function resize(): void {
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    const cw = canvas.clientWidth || window.innerWidth
    const ch = canvas.clientHeight || window.innerHeight
    canvas.width = Math.round(cw * dpr)
    canvas.height = Math.round(ch * dpr)
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

    w = cw
    h = ch
    centreX = w / 2
    horizonY = h * C.HORIZON
    focal = C.FOCAL * h

    sky = ctx.createLinearGradient(0, 0, 0, horizonY)
    sky.addColorStop(0, '#000a11')
    sky.addColorStop(0.62, C.PALETTE.bg)
    sky.addColorStop(1, '#03263c')

    ground = ctx.createLinearGradient(0, horizonY, 0, h)
    ground.addColorStop(0, '#031a26')
    ground.addColorStop(1, '#01080d')

    // The tarmac has to read from two metres away in a bright exhibition hall,
    // so it sits clearly above the surrounding ground rather than near-black.
    tarmac = ctx.createLinearGradient(
      0,
      groundY(scaleAt(C.SPAWN_Z + 40)),
      0,
      groundY(scaleAt(NEAR_Z)),
    )
    tarmac.addColorStop(0, '#08202f')
    tarmac.addColorStop(1, '#123449')

    headlights = ctx.createLinearGradient(0, groundY(scaleAt(46)), 0, groundY(scaleAt(0)))
    headlights.addColorStop(0, 'rgba(147,197,253,0)')
    headlights.addColorStop(1, 'rgba(147,197,253,0.16)')

    vignette = ctx.createRadialGradient(centreX, h * 0.55, h * 0.24, centreX, h * 0.55, h * 0.78)
    vignette.addColorStop(0, 'rgba(0,0,0,0)')
    vignette.addColorStop(1, 'rgba(0,0,0,0.42)')

    bloomBucket = -1

    const r = rng(1337)
    stars = Array.from({ length: 70 }, () => ({
      x: r() * w,
      y: r() * horizonY * 0.92,
      r: 0.4 + r() * 1.1,
      a: 0.18 + r() * 0.5,
    }))

    const b = rng(90210)
    skyline = []
    let x = -40
    while (x < w + 40) {
      const bw = 16 + b() * 46
      skyline.push({ x, w: bw, h: 14 + b() * 74 })
      x += bw + 4 + b() * 22
    }
  }

  /** Pixels per metre at distance `z`. */
  function scaleAt(z: number): number {
    return focal / Math.max(2, z + CAM_Z)
  }

  function groundY(scale: number): number {
    return horizonY + C.CAM_HEIGHT * scale
  }

  /* ------------------------------------------------------------- layers -- */

  function drawSky(state: GameState): void {
    ctx.fillStyle = sky as CanvasGradient
    ctx.fillRect(0, 0, w, horizonY)

    for (const s of stars) {
      ctx.globalAlpha = s.a
      ctx.fillStyle = '#cfe8ff'
      ctx.fillRect(s.x, s.y, s.r, s.r)
    }
    ctx.globalAlpha = 1

    // Horizon bloom: brighter the faster we go. Rebuilt in 20 speed buckets
    // only, so a 60 fps run never allocates a gradient per frame.
    const heat = (state.speed - C.SPEED_START) / (C.SPEED_MAX - C.SPEED_START)
    const bucket = Math.round(heat * 20)
    if (bucket !== bloomBucket || !bloom) {
      bloomBucket = bucket
      bloom = ctx.createRadialGradient(centreX, horizonY, 0, centreX, horizonY, h * 0.34)
      bloom.addColorStop(0, `rgba(59,130,246,${(0.2 + (bucket / 20) * 0.28).toFixed(3)})`)
      bloom.addColorStop(1, 'rgba(59,130,246,0)')
    }
    ctx.fillStyle = bloom
    ctx.fillRect(0, 0, w, horizonY + 4)

    // Parallax skyline, drifting sideways with the truck's lateral position.
    const drift = -state.x * 3.2
    ctx.fillStyle = '#01202f'
    for (const b of skyline) {
      ctx.fillRect(b.x + drift, horizonY - b.h, b.w, b.h)
    }
    ctx.fillStyle = 'rgba(59,130,246,0.35)'
    for (const b of skyline) {
      ctx.fillRect(b.x + drift, horizonY - b.h, b.w, 1)
    }
  }

  function drawRoad(state: GameState): void {
    ctx.fillStyle = ground as CanvasGradient
    ctx.fillRect(0, horizonY, w, h - horizonY)

    const farZ = C.SPAWN_Z + 40
    const sf = scaleAt(farZ)
    const sn = scaleAt(NEAR_Z)
    const yf = groundY(sf)
    const yn = groundY(sn)

    ctx.beginPath()
    ctx.moveTo(centreX - ROAD_HALF * sf, yf)
    ctx.lineTo(centreX + ROAD_HALF * sf, yf)
    ctx.lineTo(centreX + ROAD_HALF * sn, yn)
    ctx.lineTo(centreX - ROAD_HALF * sn, yn)
    ctx.closePath()
    ctx.fillStyle = tarmac as CanvasGradient
    ctx.fill()

    // Scrolling lane dashes between the three lanes.
    const phase = state.distance % DASH_PERIOD
    for (const edge of [-C.LANE_WIDTH / 2, C.LANE_WIDTH / 2]) {
      for (let i = 0; i < 26; i++) {
        const zNear = i * DASH_PERIOD - phase
        const zFar = zNear + DASH_LENGTH
        if (zFar < NEAR_Z) continue
        const a = scaleAt(Math.max(zNear, NEAR_Z))
        const b = scaleAt(zFar)
        const ya = groundY(a)
        const yb = groundY(b)
        const halfA = Math.max(0.5, 0.1 * a)
        const halfB = Math.max(0.3, 0.1 * b)
        ctx.beginPath()
        ctx.moveTo(centreX + edge * b - halfB, yb)
        ctx.lineTo(centreX + edge * b + halfB, yb)
        ctx.lineTo(centreX + edge * a + halfA, ya)
        ctx.lineTo(centreX + edge * a - halfA, ya)
        ctx.closePath()
        ctx.fillStyle = `rgba(226,240,255,${0.2 + 0.45 * (b / sn)})`
        ctx.fill()
      }
    }

    // Neon kerbs.
    for (const side of [-1, 1]) {
      ctx.beginPath()
      ctx.moveTo(centreX + side * ROAD_HALF * sf, yf)
      ctx.lineTo(centreX + side * (ROAD_HALF + 0.28) * sf, yf)
      ctx.lineTo(centreX + side * (ROAD_HALF + 0.28) * sn, yn)
      ctx.lineTo(centreX + side * ROAD_HALF * sn, yn)
      ctx.closePath()
      ctx.fillStyle = 'rgba(96,165,250,0.8)'
      ctx.fill()
    }

    // Guard-rail posts, spaced along the road, both sides.
    const postPhase = state.distance % 22
    for (let i = 0; i < 16; i++) {
      const z = i * 22 - postPhase
      if (z < NEAR_Z + 2) continue
      const s = scaleAt(z)
      const y = groundY(s)
      const height = 1.25 * s
      for (const side of [-1, 1]) {
        const px = centreX + side * (ROAD_HALF + 0.7) * s
        ctx.fillStyle = 'rgba(96,165,250,0.22)'
        ctx.fillRect(px - 0.12 * s, y - height, 0.24 * s, height)
        ctx.fillStyle = 'rgba(96,165,250,0.5)'
        ctx.fillRect(px - 0.22 * s, y - height, 0.44 * s, Math.max(1, 0.16 * s))
      }
    }
  }

  /** Draws an axis-aligned box in world space: far face, sides, top, near face. */
  function drawBox(
    x: number,
    z: number,
    halfWidth: number,
    height: number,
    depth: number,
    color: string,
    glow: string,
    alpha: number,
  ): void {
    const zn = Math.max(NEAR_Z, z - depth / 2)
    const zf = z + depth / 2
    if (zf < NEAR_Z) return

    const sn = scaleAt(zn)
    const sf = scaleAt(zf)
    const yn = groundY(sn)
    const yf = groundY(sf)
    const xnl = centreX + (x - halfWidth) * sn
    const xnr = centreX + (x + halfWidth) * sn
    const xfl = centreX + (x - halfWidth) * sf
    const xfr = centreX + (x + halfWidth) * sf
    const tn = yn - height * sn
    const tf = yf - height * sf

    ctx.globalAlpha = alpha

    // Contact shadow.
    ctx.fillStyle = 'rgba(0,0,0,0.45)'
    ctx.beginPath()
    ctx.ellipse(centreX + x * sn, yn, halfWidth * 1.15 * sn, 0.4 * sn, 0, 0, Math.PI * 2)
    ctx.fill()

    // Far face.
    ctx.fillStyle = shade(color, 0.45)
    ctx.fillRect(xfl, tf, xfr - xfl, yf - tf)

    // Side walls.
    ctx.fillStyle = shade(color, 0.62)
    ctx.beginPath()
    ctx.moveTo(xfl, tf)
    ctx.lineTo(xnl, tn)
    ctx.lineTo(xnl, yn)
    ctx.lineTo(xfl, yf)
    ctx.closePath()
    ctx.fill()
    ctx.beginPath()
    ctx.moveTo(xfr, tf)
    ctx.lineTo(xnr, tn)
    ctx.lineTo(xnr, yn)
    ctx.lineTo(xfr, yf)
    ctx.closePath()
    ctx.fill()

    // Top.
    ctx.fillStyle = shade(color, 0.8)
    ctx.beginPath()
    ctx.moveTo(xfl, tf)
    ctx.lineTo(xfr, tf)
    ctx.lineTo(xnr, tn)
    ctx.lineTo(xnl, tn)
    ctx.closePath()
    ctx.fill()

    // Near face plus its glow.
    ctx.shadowColor = glow
    ctx.shadowBlur = Math.min(34, 8 + sn * 0.6)
    ctx.fillStyle = color
    ctx.fillRect(xnl, tn, xnr - xnl, yn - tn)
    ctx.shadowBlur = 0
    ctx.globalAlpha = 1
  }

  /** Darkens a #rrggbb colour by `k` (0 = black, 1 = unchanged). */
  function shade(hex: string, k: number): string {
    const n = parseInt(hex.slice(1), 16)
    const r = Math.round(((n >> 16) & 255) * k)
    const g = Math.round(((n >> 8) & 255) * k)
    const b = Math.round((n & 255) * k)
    return `rgb(${r},${g},${b})`
  }

  function drawEntity(e: Entity): void {
    const v = C.VISUALS[e.kind]
    if (e.z + CAM_Z < 6) return

    const fade = e.spent ? e.fx : 1
    if (fade <= 0) return
    const distanceFade = Math.min(1, Math.max(0.12, 1.35 - e.z / C.SPAWN_Z))
    const x = C.LANE_X[e.lane]
    const lift = e.group === 'obstacle' ? 0 : 0.9 + Math.sin(shimmer * 2.4 + e.id) * 0.18
    const height = v.height * (e.spent ? 1 + (1 - e.fx) * 0.8 : 1)

    if (lift > 0) {
      // Floating pickups/power-ups: draw the box raised off the tarmac.
      const s = scaleAt(e.z)
      const y = groundY(s)
      ctx.save()
      ctx.translate(0, -lift * s)
      drawBox(x, e.z, v.halfWidth, height, v.depth, v.color, v.glow, fade * distanceFade)
      ctx.restore()
      // Light pool underneath.
      ctx.globalAlpha = 0.25 * fade * distanceFade
      ctx.fillStyle = v.glow
      ctx.beginPath()
      ctx.ellipse(centreX + x * s, y, v.halfWidth * 1.4 * s, 0.35 * s, 0, 0, Math.PI * 2)
      ctx.fill()
      ctx.globalAlpha = 1
    } else {
      drawBox(x, e.z, v.halfWidth, height, v.depth, v.color, v.glow, fade * distanceFade)
    }

    // Glyph on the near face, only when it would be legible.
    const s = scaleAt(e.z - v.depth / 2)
    const fontSize = v.halfWidth * s * 0.85
    if (fontSize > 9) {
      ctx.globalAlpha = fade * distanceFade
      ctx.fillStyle = '#001018'
      ctx.font = `700 ${Math.round(fontSize)}px 'Space Grotesk', system-ui, sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      const y = groundY(s) - (height * s) / 2 - (lift > 0 ? lift * s : 0)
      ctx.fillText(v.glyph, centreX + x * s, y)
      ctx.globalAlpha = 1
    }
  }

  /**
   * The camera sits behind the rig, so what the player sees is the BACK of a
   * trailer: rear doors, tail lights, plate, and the cab roof peeking over the
   * top. The cab is further from the lens than the trailer, so it is painted
   * first and the trailer covers it.
   */
  function drawTruck(state: GameState): void {
    const x = state.x
    const tilt = (C.LANE_X[state.lane] - x) * 0.06
    const cabZ = -1.7
    const trailerZ = -6.2
    const trailerHalf = 1.3
    const trailerHeight = 3.2
    const rearZ = trailerZ - 3.3

    // Head-light pool thrown forward onto the tarmac.
    const sFar = scaleAt(52)
    const sNear = scaleAt(2)
    ctx.fillStyle = headlights as CanvasGradient
    ctx.beginPath()
    ctx.moveTo(centreX + (x - 5.2) * sFar, groundY(sFar))
    ctx.lineTo(centreX + (x + 5.2) * sFar, groundY(sFar))
    ctx.lineTo(centreX + (x + 1.4) * sNear, groundY(sNear))
    ctx.lineTo(centreX + (x - 1.4) * sNear, groundY(sNear))
    ctx.closePath()
    ctx.fill()

    drawBox(x + tilt, cabZ, 1.22, 3.5, 3.2, '#1d4ed8', 'rgba(59,130,246,0.5)', 1)
    drawBox(
      x + tilt * 0.35,
      trailerZ,
      trailerHalf,
      trailerHeight,
      6.6,
      state.doublePoints > 0 ? '#0b4a5c' : '#0c2c44',
      state.doublePoints > 0 ? 'rgba(34,211,238,0.5)' : 'rgba(59,130,246,0.32)',
      1,
    )

    // Rear-door detailing on the face turned towards the camera.
    const s = scaleAt(rearZ)
    const y = groundY(s)
    const top = y - trailerHeight * s
    const left = centreX + (x + tilt * 0.35 - trailerHalf) * s
    const right = centreX + (x + tilt * 0.35 + trailerHalf) * s
    const width = right - left

    ctx.fillStyle = 'rgba(255,255,255,0.06)'
    ctx.fillRect(left + width * 0.49, top + 0.25 * s, Math.max(1, width * 0.02), (y - top) * 0.82)

    // Brand band.
    ctx.fillStyle = 'rgba(59,130,246,0.85)'
    ctx.fillRect(left + width * 0.1, top + 0.55 * s, width * 0.8, Math.max(2, 0.42 * s))
    if (width > 96) {
      ctx.fillStyle = '#001018'
      ctx.font = `700 ${Math.round(0.34 * s)}px 'Space Grotesk', system-ui, sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText('SOKAR', centreX + x * s, top + 0.55 * s + 0.21 * s)
    }

    // Tail lights and plate.
    ctx.fillStyle = '#ff4d6d'
    ctx.shadowColor = 'rgba(255,77,109,0.8)'
    ctx.shadowBlur = 12
    ctx.fillRect(left + width * 0.07, y - 0.95 * s, width * 0.15, 0.32 * s)
    ctx.fillRect(right - width * 0.22, y - 0.95 * s, width * 0.15, 0.32 * s)
    ctx.shadowBlur = 0
    ctx.fillStyle = 'rgba(226,232,240,0.85)'
    ctx.fillRect(centreX + x * s - width * 0.08, y - 0.92 * s, width * 0.16, 0.26 * s)

    // Roof marker lamps.
    ctx.fillStyle = 'rgba(255,193,86,0.8)'
    for (const f of [0.18, 0.5, 0.82]) {
      ctx.fillRect(
        left + width * f - width * 0.02,
        top - 0.06 * s,
        width * 0.04,
        Math.max(1, 0.1 * s),
      )
    }

    if (state.autopilot > 0) {
      // Bobi aura: the assistant is driving.
      const pulse = 0.5 + Math.sin(shimmer * 7) * 0.22
      ctx.globalAlpha = pulse
      ctx.strokeStyle = C.PALETTE.accent2
      ctx.lineWidth = Math.max(2, 0.1 * s)
      ctx.beginPath()
      ctx.ellipse(centreX + x * s, y, 2.6 * s, 0.9 * s, 0, 0, Math.PI * 2)
      ctx.stroke()
      ctx.globalAlpha = 1
    }
  }

  function drawSpeedLines(state: GameState): void {
    const heat = (state.speed - C.SPEED_START) / (C.SPEED_MAX - C.SPEED_START)
    if (heat <= 0.12) return
    ctx.globalAlpha = Math.min(0.32, heat * 0.4)
    ctx.strokeStyle = '#93c5fd'
    ctx.lineWidth = 1.4
    for (let i = 0; i < 10; i++) {
      const t = ((shimmer * (1.4 + heat * 2.6) + i * 0.1) % 1) ** 2
      const side = i % 2 === 0 ? -1 : 1
      const z = 120 - t * 130
      const s = scaleAt(z)
      const px = centreX + side * (ROAD_HALF + 1.4 + (i % 5)) * s
      const py = groundY(s) - 1.6 * s
      ctx.beginPath()
      ctx.moveTo(px, py)
      ctx.lineTo(px + side * 22 * t, py + 10 * t)
      ctx.stroke()
    }
    ctx.globalAlpha = 1
  }

  function drawOverlays(state: GameState): void {
    if (state.doublePoints > 0) {
      ctx.fillStyle = 'rgba(34,211,238,0.06)'
      ctx.fillRect(0, 0, w, h)
    }
    if (state.crashFx > 0) {
      ctx.fillStyle = `rgba(255,77,109,${0.55 * state.crashFx})`
      ctx.fillRect(0, 0, w, h)
    }
    // Vignette keeps the eye on the lanes.
    ctx.fillStyle = vignette as CanvasGradient
    ctx.fillRect(0, 0, w, h)
  }

  function draw(state: GameState, elapsed: number): void {
    shimmer += elapsed
    drawSky(state)
    drawRoad(state)
    drawSpeedLines(state)

    // Entities are appended in ascending z, so a reverse walk paints far-to-near
    // without allocating or sorting every frame.
    for (let i = state.entities.length - 1; i >= 0; i--) drawEntity(state.entities[i])

    drawTruck(state)
    drawOverlays(state)
  }

  resize()
  return { resize, draw }
}
